import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { toFeaturedSlug } from '../pages/FeaturedStay';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_SLUGS = {
  'bluehouse': 'bluehouse',
  'eagle': 'eagles-nest',
  'tra ': 'tra',
  'tra,': 'tra',
  'himalayan homestead': 'homestead',
  'villa aavaas': 'villaaavaas',
  'srirangam': 'srirangam',
};

const getStayLink = (stay) => {
  const name = (stay.display_name || '').toLowerCase();
  if (stay.partnership_status === 'partner') {
    for (const [key, slug] of Object.entries(PARTNER_SLUGS)) {
      if (name.includes(key)) return `/collective/${slug}`;
    }
    return '/discovered';
  }
  return `/stay/${toFeaturedSlug(stay.display_name)}`;
};

const AISearchBar = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [placeholder, setPlaceholder] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Responsive placeholder
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 480) setPlaceholder('Search Himalayan stays...');
      else if (w < 768) setPlaceholder("Try 'riverside cabin' or 'snowy retreat'");
      else setPlaceholder("Discovering new Himalayan experiences — try 'offbeat stay near a river' or 'cabin with mountain views'");
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { setIsOpen(false); setQuery(''); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const handleSearch = async () => {
    if (!query.trim() || query.trim().length < 3) return;
    setLoading(true);
    setIsOpen(true);
    try {
      const res = await fetch(`${API}/api/ai/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim() }),
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setResults({ matches: [], has_matches: false, message: 'Something went wrong. Please try again.', demand_captured: false });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleResultClick = (stay) => {
    const link = getStayLink(stay);
    setIsOpen(false);
    setQuery('');
    setResults(null);
    navigate(link);
  };

  return (
    <div ref={containerRef} className="relative flex-1" data-testid="ai-search-container">
      {/* Search input */}
      <div className="flex items-center gap-2 sm:gap-2.5 bg-gray-50 border border-gray-200 rounded-full px-3 sm:px-4 py-2 transition-all focus-within:border-[#2C5F8D] focus-within:bg-white focus-within:shadow-sm">
        <Sparkles className="w-3.5 h-3.5 text-[#2C5F8D] flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent text-xs text-gray-700 placeholder-gray-400 outline-none min-w-0"
          placeholder={placeholder}
          data-testid="ai-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults(null); setIsOpen(false); }} className="text-gray-400 hover:text-gray-600">
            <X className="w-3 h-3" />
          </button>
        )}
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 text-[#2C5F8D] animate-spin" />
        ) : (
          <button
            onClick={handleSearch}
            disabled={!query.trim() || query.trim().length < 3}
            className="text-gray-400 hover:text-[#2C5F8D] disabled:opacity-30 transition-colors"
            data-testid="ai-search-submit"
          >
            <Search className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results && (
        <div
          className="absolute top-full mt-2 right-0 left-0 sm:left-auto sm:w-[400px] bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-[999] animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="ai-search-results"
        >
          {/* AI message */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-50">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-[#2C5F8D] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                {results.message}
              </p>
            </div>
          </div>

          {/* Matched stays */}
          {results.has_matches && results.matches.length > 0 ? (
            <div className="max-h-[360px] overflow-y-auto">
              {results.matches.map((stay, i) => (
                <button
                  key={i}
                  onClick={() => handleResultClick(stay)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left group"
                  data-testid={`ai-result-${i}`}
                >
                  {stay.hero_image && (
                    <img
                      src={stay.hero_image}
                      alt={stay.display_name}
                      className="w-14 h-14 rounded-lg object-cover img-enhance flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {stay.display_name?.split(',')[0]}
                      </h4>
                      {stay.partnership_status === 'partner' && (
                        <span className="text-[9px] font-bold tracking-wider text-[#2C5F8D] uppercase">C100</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{stay.reason}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#2C5F8D] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            /* No match — demand captured */
            <div className="px-5 py-6 text-center">
              <p className="text-sm text-gray-500 mb-2">
                We don't have an exact match yet — but we're on it.
              </p>
              <p className="text-xs text-gray-400">
                Your search has been added to our radar. We're constantly scouting new offbeat stays across the Himalayas.
              </p>
              {results.demand_captured && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-[#2C5F8D]/5 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2C5F8D] animate-pulse" />
                  <span className="text-[10px] font-medium text-[#2C5F8D] tracking-wide uppercase">Added to our radar</span>
                </div>
              )}
            </div>
          )}

          {/* Browse all CTA */}
          <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50">
            <button
              onClick={() => { setIsOpen(false); setQuery(''); navigate('/discovered'); }}
              className="w-full text-xs text-gray-500 hover:text-[#2C5F8D] transition-colors text-center"
              data-testid="ai-browse-all"
            >
              Browse all {49} stays &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISearchBar;
